import { create } from "zustand";

import { logger } from "@/telemetry";

/**
 * UI Preferences Store
 *
 * Manages user interface preferences with localStorage persistence.
 * Provides a single source of truth for UI toggles across the application.
 */

/**
 * Export font options for Word document exports.
 * "brand" uses the current brand's font family (e.g. Poppins for WebuildAI, Helvetica Neue for National Grid)
 */
export type ExportFontOption = "brand" | "Poppins" | "Calibri" | "Arial" | "Times New Roman";

/**
 * Per-control pinned state for the chat input toolbar.
 * Each key maps to whether that control is always visible (pinned) in the toolbar.
 */
export interface PinnedChatControls {
  prompts: boolean;
  prompto: boolean;
  search: boolean;
  knowledgeHub: boolean;
  configuration: boolean;
  modelSelector: boolean;
}

/** Union of all valid pinned-control keys. */
export type PinnedChatControlKey = keyof PinnedChatControls;

/**
 * Collapsed state of the Workflow Builder's three panes (list, build-agent
 * chat, canvas). The chat and canvas panes are mutually exclusive collapse
 * targets — collapsing one hands its width to the other — so both being
 * collapsed at once would leave the shell with nothing to show.
 */
export interface WorkflowBuilderCollapsedPanes {
  list: boolean;
  chat: boolean;
  canvas: boolean;
}

/** Union of all valid Workflow Builder pane keys. */
export type WorkflowBuilderPane = keyof WorkflowBuilderCollapsedPanes;

/**
 * localStorage key for per-workflow run notification preferences (LSEG-107).
 *
 * This is a device-local display preference, not a subscription. `Notification`
 * in agent-platform has no `workflowId`, no subscription entity exists anywhere,
 * and notifications were explicitly ruled out of scope on the backend side, so
 * nothing is delivered to anyone. It lives here rather than on the workflow record
 * precisely because it is not something the backend honours, and the confirmation
 * dialog says so before it is flipped in either direction.
 */
const AUTOMATION_NOTIFY_KEY = "pathway.automations.notify";

/** localStorage key the Workflow Builder's collapsed-pane state is persisted under. */
const WORKFLOW_BUILDER_COLLAPSED_PANES_KEY = "pathway.workflowBuilder.collapsedPanes";

/** Default value for WorkflowBuilderCollapsedPanes. All panes start expanded. */
const DEFAULT_WORKFLOW_BUILDER_COLLAPSED_PANES: WorkflowBuilderCollapsedPanes = {
  list: false,
  chat: false,
  canvas: false,
};

/**
 * Clamps an illegal Workflow Builder collapsed-pane state where both the
 * chat and canvas panes are collapsed simultaneously. The shell needs at
 * least one of the two visible, so chat is forced back open in that case.
 * Applied both when hydrating from localStorage and whenever a pane's
 * collapsed state is set, so the invariant always holds.
 */
function clampWorkflowBuilderCollapsedPanes(
  panes: WorkflowBuilderCollapsedPanes,
): WorkflowBuilderCollapsedPanes {
  if (panes.chat && panes.canvas) {
    return { ...panes, chat: false };
  }

  return panes;
}

/**
 * Default value for PinnedChatControls.
 *
 * The model selector is the one control shown by default, because choosing a
 * model is part of starting a chat rather than an advanced extra, and a user
 * who has never opened Settings should not have to go looking for it. Every
 * other control stays hidden until the user pins it from the "/" command menu
 * or Settings.
 *
 * This is the initial value only. It is read when nothing has been persisted
 * under "chat-input-pinned-controls", so a user who has already turned the
 * model selector off keeps that choice: their stored object is hydrated
 * verbatim and never merged with these defaults.
 */
const DEFAULT_PINNED_CHAT_CONTROLS: PinnedChatControls = {
  prompts: false,
  prompto: false,
  search: false,
  knowledgeHub: false,
  configuration: false,
  modelSelector: true,
};

/**
 * Font option configuration with system-safe metadata.
 * isSystemSafe indicates whether the font is pre-installed on most computers.
 *
 * IMPORTANT: Bundled fonts (Poppins, Helvetica Neue) are NOT system-safe for exports.
 * They only work in the web app via @font-face. DOCX files don't embed fonts,
 * so recipients without these fonts installed will see a substituted font.
 */
export const EXPORT_FONT_OPTIONS: {
  value: ExportFontOption;
  label: string;
  isSystemSafe: boolean;
}[] = [
  { value: "brand", label: "Use Brand Font", isSystemSafe: false }, // Bundled brand font - NOT pre-installed
  { value: "Poppins", label: "Poppins", isSystemSafe: false }, // NOT pre-installed on most systems
  { value: "Calibri", label: "Calibri", isSystemSafe: true }, // Pre-installed on Windows, macOS
  { value: "Arial", label: "Arial", isSystemSafe: true }, // Pre-installed on all systems
  { value: "Times New Roman", label: "Times New Roman", isSystemSafe: true }, // Pre-installed on all systems
];

/**
 * Page size options for Word document exports.
 * A4 is the default (standard international size).
 * Labels and dimensions use i18n translation keys (pageSize.*).
 */
export type ExportPageSize = "A4" | "Letter" | "Legal";

export const EXPORT_PAGE_SIZE_OPTIONS: {
  value: ExportPageSize;
  labelKey: string;
  dimensionsKey: string;
}[] = [
  { value: "A4", labelKey: "pageSize.A4", dimensionsKey: "pageSize.A4.dimensions" },
  { value: "Letter", labelKey: "pageSize.Letter", dimensionsKey: "pageSize.Letter.dimensions" },
  { value: "Legal", labelKey: "pageSize.Legal", dimensionsKey: "pageSize.Legal.dimensions" },
];

export interface UIPreferencesState {
  // Line numbers display preference for code artefacts
  showLineNumbers: boolean;

  // Export font preference for Word documents
  exportFontFamily: ExportFontOption;

  // Export page size preference for Word documents
  exportPageSize: ExportPageSize;

  // Show advanced download options (PNG, CSV, Markdown, HTML, Text)
  showAdvancedDownloadOptions: boolean;

  // Whether the chat input is in simple (single-line) mode
  simpleChatInput: boolean;

  // Which chat input toolbar controls are pinned (always visible)
  pinnedChatControls: PinnedChatControls;

  // Whether web search is enabled by default for new chat sessions
  webSearchDefault: boolean;

  // Whether knowledge hub is enabled by default for new chat sessions
  knowledgeHubDefault: boolean;

  // Whether the simple chat input mode hint banner has been dismissed
  simpleChatInputHintDismissed: boolean;

  // Whether active-feature chips (Web Search, Knowledge Hub) are shown
  // next to the model selector in the action row (default false)
  showActiveFeatureChips: boolean;

  // Which Workflow Builder panes (list, build-agent chat, canvas) are collapsed
  workflowBuilderCollapsedPanes: WorkflowBuilderCollapsedPanes;

  // Workflow id to whether run notifications are wanted for it. Device-local and
  // not delivered anywhere; see AUTOMATION_NOTIFY_KEY.
  automationNotify: Record<string, boolean>;

  // Actions
  toggleLineNumbers: () => void;
  setExportFontFamily: (font: ExportFontOption) => void;
  setExportPageSize: (size: ExportPageSize) => void;
  toggleAdvancedDownloadOptions: () => void;
  toggleSimpleChatInput: () => void;
  setSimpleChatInput: (value: boolean) => void;
  setPinnedChatControl: (key: PinnedChatControlKey, value: boolean) => void;
  setWebSearchDefault: (value: boolean) => void;
  setKnowledgeHubDefault: (value: boolean) => void;
  dismissSimpleChatInputHint: () => void;
  setShowActiveFeatureChips: (value: boolean) => void;
  setWorkflowBuilderPaneCollapsed: (pane: WorkflowBuilderPane, collapsed: boolean) => void;
  setAutomationNotify: (workflowId: string, notify: boolean) => void;
}

const VALID_EXPORT_FONTS: ExportFontOption[] = [
  "brand",
  "Poppins",
  "Calibri",
  "Arial",
  "Times New Roman",
];
const VALID_EXPORT_PAGE_SIZES: ExportPageSize[] = ["A4", "Letter", "Legal"];
const PINNED_CHAT_CONTROL_KEYS: PinnedChatControlKey[] = [
  "prompts",
  "prompto",
  "search",
  "knowledgeHub",
  "configuration",
  "modelSelector",
];
const WORKFLOW_BUILDER_PANES: WorkflowBuilderPane[] = ["list", "chat", "canvas"];

/**
 * Read a stored JSON boolean, falling back when the key is absent, empty,
 * unparseable or holds anything other than a boolean. The setters only ever
 * write booleans, so only a hand-edited or corrupted value takes the fallback.
 */
function readStoredBoolean(key: string, fallback: boolean): boolean {
  try {
    const stored = localStorage.getItem(key);

    return (stored ? parseStoredBoolean(stored) : undefined) ?? fallback;
  } catch {
    return fallback;
  }
}

/** A stored JSON boolean, or undefined when the stored JSON is anything else. */
function parseStoredBoolean(stored: string): boolean | undefined {
  const parsed: unknown = JSON.parse(stored);

  return typeof parsed === "boolean" ? parsed : undefined;
}

/** Whether a value is one of the given options. */
function isOneOf<T extends string>(value: unknown, options: readonly T[]): value is T {
  return options.some((option) => option === value);
}

/** Read a stored option, falling back unless it is one of the valid options. */
function readStoredOption<T extends string>(key: string, validOptions: T[], fallback: T): T {
  try {
    const stored = localStorage.getItem(key);

    if (stored) {
      const parsed: unknown = JSON.parse(stored);

      // Validate that the stored value is a valid option
      if (isOneOf(parsed, validOptions)) {
        return parsed;
      }
    }

    return fallback;
  } catch {
    return fallback;
  }
}

/** Whether a value is a plain object with a boolean under every one of `keys`. */
function hasBooleanFlags<K extends string>(
  value: unknown,
  keys: readonly K[],
): value is Record<K, boolean> {
  if (value === null || typeof value !== "object" || Array.isArray(value)) return false;

  return keys.every((k) => typeof Reflect.get(value, k) === "boolean");
}

/**
 * Read a stored object of boolean flags. Returns it as stored when it is an
 * object with a boolean under every expected key, and undefined on any shape
 * mismatch or parse error. The result is typed by the keys that were checked,
 * so a caller cannot claim more keys than it validated.
 */
function readStoredFlags<K extends string>(
  key: string,
  expectedKeys: readonly K[],
): Record<K, boolean> | undefined {
  try {
    const stored = localStorage.getItem(key);

    if (!stored) return undefined;

    const parsed: unknown = JSON.parse(stored);

    return hasBooleanFlags(parsed, expectedKeys) ? parsed : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Web search default. Migration: if "web-search-default" is absent, reads the
 * legacy "webSearchEnabled" key.
 */
function readWebSearchDefault(): boolean {
  try {
    const stored = localStorage.getItem("web-search-default");

    if (stored !== null) {
      return parseStoredBoolean(stored) ?? false;
    }

    // Legacy migration: fall back to "webSearchEnabled" if present
    const legacy = localStorage.getItem("webSearchEnabled");

    if (legacy !== null) {
      return parseStoredBoolean(legacy) ?? false;
    }

    return false;
  } catch {
    return false;
  }
}

/**
 * Per-workflow notification preferences. Only boolean entries are kept, so a
 * corrupt or hand-edited value cannot put a non-boolean into the map and reach
 * a switch as an object.
 */
function readAutomationNotify(): Record<string, boolean> {
  try {
    const stored = localStorage.getItem(AUTOMATION_NOTIFY_KEY);

    if (!stored) return {};

    const parsed: unknown = JSON.parse(stored);

    if (parsed === null || typeof parsed !== "object" || Array.isArray(parsed)) return {};

    const cleaned: Record<string, boolean> = {};

    for (const [key, value] of Object.entries(parsed as Record<string, unknown>)) {
      if (typeof value === "boolean") cleaned[key] = value;
    }

    return cleaned;
  } catch {
    return {};
  }
}

/** Persist a preference, logging rather than throwing when localStorage refuses. */
function persistPreference(key: string, value: unknown, failureMessage: string): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    logger.warn(failureMessage, {
      error: String(error),
    });
  }
}

export const useUIPreferences = create<UIPreferencesState>((set, get) => ({
  // Initialize from localStorage
  showLineNumbers: readStoredBoolean("artefact-show-line-numbers", false),

  // Initialize export font from localStorage (default to "brand")
  exportFontFamily: readStoredOption("export-font-family", VALID_EXPORT_FONTS, "brand"),

  // Initialize export page size from localStorage (default to "A4")
  exportPageSize: readStoredOption("export-page-size", VALID_EXPORT_PAGE_SIZES, "A4"),

  // Initialize advanced download options from localStorage (default to false)
  showAdvancedDownloadOptions: readStoredBoolean("show-advanced-download-options", false),

  // Initialise simple chat input mode from localStorage (default to true)
  simpleChatInput: readStoredBoolean("chat-input-simple-mode", true),

  // Initialise pinned chat controls from localStorage (DEFAULT_PINNED_CHAT_CONTROLS
  // when nothing is stored, which shows the model selector and hides the rest).
  // Validates that the stored value is an object with exactly the expected keys;
  // falls back to those same defaults on any shape mismatch or parse error.
  // A valid stored object is returned verbatim rather than merged over the
  // defaults, which is what keeps a deliberate choice to hide the model
  // selector from being overridden by a later change to the default.
  pinnedChatControls: readStoredFlags("chat-input-pinned-controls", PINNED_CHAT_CONTROL_KEYS) ?? {
    ...DEFAULT_PINNED_CHAT_CONTROLS,
  },

  // Initialise web search default from localStorage.
  webSearchDefault: readWebSearchDefault(),

  // Initialise knowledge hub default from localStorage (default to false)
  knowledgeHubDefault: readStoredBoolean("knowledge-hub-default", false),

  // Initialise simple chat input hint dismissed state from localStorage (default to false)
  simpleChatInputHintDismissed: readStoredBoolean("chat-input-hint-dismissed", false),

  // Initialise active-feature chips visibility from localStorage (default to false)
  showActiveFeatureChips: readStoredBoolean("chat-input-show-active-chips", false),

  // Initialise Workflow Builder collapsed panes from localStorage (default all expanded).
  // Validates that the stored value is an object with exactly the expected keys;
  // falls back to all-expanded on any shape mismatch or parse error. The illegal
  // chat+canvas-both-collapsed state is clamped on every hydration.
  workflowBuilderCollapsedPanes: (() => {
    const stored = readStoredFlags(WORKFLOW_BUILDER_COLLAPSED_PANES_KEY, WORKFLOW_BUILDER_PANES);

    return stored
      ? clampWorkflowBuilderCollapsedPanes(stored)
      : { ...DEFAULT_WORKFLOW_BUILDER_COLLAPSED_PANES };
  })(),

  // Initialise per-workflow notification preferences from localStorage (default none).
  automationNotify: readAutomationNotify(),

  // Toggle line numbers and persist to localStorage
  toggleLineNumbers: () => {
    const newValue = !get().showLineNumbers;

    set({ showLineNumbers: newValue });
    persistPreference(
      "artefact-show-line-numbers",
      newValue,
      "Failed to save line numbers preference to localStorage",
    );
  },

  // Set export font family and persist to localStorage
  setExportFontFamily: (font: ExportFontOption) => {
    set({ exportFontFamily: font });
    persistPreference(
      "export-font-family",
      font,
      "Failed to save export font preference to localStorage",
    );
  },

  // Set export page size and persist to localStorage
  setExportPageSize: (size: ExportPageSize) => {
    set({ exportPageSize: size });
    persistPreference(
      "export-page-size",
      size,
      "Failed to save export page size preference to localStorage",
    );
  },

  // Toggle advanced download options and persist to localStorage
  toggleAdvancedDownloadOptions: () => {
    const newValue = !get().showAdvancedDownloadOptions;

    set({ showAdvancedDownloadOptions: newValue });
    persistPreference(
      "show-advanced-download-options",
      newValue,
      "Failed to save advanced download options preference to localStorage",
    );
  },

  // Toggle simple chat input mode and persist to localStorage
  toggleSimpleChatInput: () => {
    const newValue = !get().simpleChatInput;

    set({ simpleChatInput: newValue });
    persistPreference(
      "chat-input-simple-mode",
      newValue,
      "Failed to save simple chat input preference to localStorage",
    );
  },

  // Set simple chat input mode explicitly and persist to localStorage
  setSimpleChatInput: (value: boolean) => {
    set({ simpleChatInput: value });
    persistPreference(
      "chat-input-simple-mode",
      value,
      "Failed to save simple chat input preference to localStorage",
    );
  },

  // Update a single pinned chat control and persist the whole object to localStorage
  setPinnedChatControl: (key: PinnedChatControlKey, value: boolean) => {
    const updated: PinnedChatControls = { ...get().pinnedChatControls, [key]: value };

    set({ pinnedChatControls: updated });
    persistPreference(
      "chat-input-pinned-controls",
      updated,
      "Failed to save pinned chat controls preference to localStorage",
    );
  },

  // Set web search default and persist to localStorage
  setWebSearchDefault: (value: boolean) => {
    set({ webSearchDefault: value });
    persistPreference(
      "web-search-default",
      value,
      "Failed to save web search default preference to localStorage",
    );
  },

  // Set knowledge hub default and persist to localStorage
  setKnowledgeHubDefault: (value: boolean) => {
    set({ knowledgeHubDefault: value });
    persistPreference(
      "knowledge-hub-default",
      value,
      "Failed to save knowledge hub default preference to localStorage",
    );
  },

  // Dismiss the simple chat input hint banner and persist to localStorage
  dismissSimpleChatInputHint: () => {
    set({ simpleChatInputHintDismissed: true });
    persistPreference(
      "chat-input-hint-dismissed",
      true,
      "Failed to save simple chat input hint dismissed state to localStorage",
    );
  },

  // Set active-feature chips visibility and persist to localStorage
  setShowActiveFeatureChips: (value: boolean) => {
    set({ showActiveFeatureChips: value });
    persistPreference(
      "chat-input-show-active-chips",
      value,
      "Failed to save active feature chips preference to localStorage",
    );
  },

  // Update a single Workflow Builder pane's collapsed state, clamp the illegal
  // chat+canvas-both-collapsed combination, and persist the whole object
  setWorkflowBuilderPaneCollapsed: (pane: WorkflowBuilderPane, collapsed: boolean) => {
    const updated = clampWorkflowBuilderCollapsedPanes({
      ...get().workflowBuilderCollapsedPanes,
      [pane]: collapsed,
    });

    set({ workflowBuilderCollapsedPanes: updated });
    persistPreference(
      WORKFLOW_BUILDER_COLLAPSED_PANES_KEY,
      updated,
      "Failed to save workflow builder collapsed panes preference to localStorage",
    );
  },

  // Set one workflow's run notification preference and persist the whole map
  setAutomationNotify: (workflowId: string, notify: boolean) => {
    const updated = { ...get().automationNotify, [workflowId]: notify };

    set({ automationNotify: updated });
    persistPreference(
      AUTOMATION_NOTIFY_KEY,
      updated,
      "Failed to save automation notify preference to localStorage",
    );
  },
}));
